import React, { useContext, useEffect, useRef } from 'react';
import { RadioButton } from '@marriott/mi-ui-library-shop';
import { TextField } from '@marriott/mi-ui-library-shop';
import { eventUtil } from '@marriott/mi-ui-library-shop';
import { StyledSpecialRatesDiv } from './index.styles';
import { SpecialRatesProps } from './index.types';
import { SPECIAL_RATES } from '../../../constants/StoreConstants';
import { setSpecialRates, setUsePoints } from '../../../store/store.actions';
import Popup from '../../atoms/popup/popup';
import PopupMain from '../../atoms/popupMain/popupMain';
import PopupHeader from '../../atoms/popupHeader/popupHeader';
import PopupFooter from '../../atoms/popupFooter/popupFooter';
import CommonUtils from '../../../utils/CommonUtils';
import clsx from 'clsx';
import { useConditionalPageContextSERP } from '../../../../../../hooks';
import {
  hardCodedAllLocale,
  SPECIAL_RATES_DONE_CLICK_TRACK,
  SPECIAL_RATES_CLOSE_CLICK_TRACK,
  SPECIAL_RATES_EDIT_CLICK_TRACK,
} from '../../index.constants';
import { ContentContext, ViewportContext, FocusContext, FormValuesContext } from '../../index.contexts';
import { getSearchFormClickTrackingLoc } from '../../../../../../modules/utils/searchFormHelper';

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface HTMLAttributes<T> {
    custom_click_track_value?: string;
  }
}

const SpecialRates: React.FC<SpecialRatesProps> = ({
  onCancel,
  onChange: changeMobileState,
  isSearchPage = false,
  isADFPage = false,
}) => {
  const compRef = useRef<HTMLDivElement>(null);
  const isMobileView = useContext(ViewportContext);
  const { formValues, setFormValues, activePopup, setActivePopup } = useContext(FormValuesContext);
  const { setIsUserInteractWithComp } = useContext(FocusContext);
  const splRatesHiddenFields = formValues?.[SPECIAL_RATES];
  const [collapseTextField, setCollapseTextField] = React.useState(false);
  const [popupOpenState, setPopupOpenState] = React.useState(false);
  /*
    Logic to ignore the group codes on ADF page, remove this once group codes are enabled for ADF
  */
  const modifiedSplRatesHiddenFields = { ...splRatesHiddenFields };
  if (modifiedSplRatesHiddenFields?.clusterCode?.toLowerCase() === 'group') {
    modifiedSplRatesHiddenFields.clusterCode = 'none';
    modifiedSplRatesHiddenFields.corporateCode = '';
  }
  const [clusterCode, setClusterCode] = React.useState<string>(modifiedSplRatesHiddenFields?.clusterCode ?? '');
  const [corpCode, setCorpCode] = React.useState<string>(modifiedSplRatesHiddenFields?.corporateCode ?? '');
  const {
    specialRatesListItems,
    ratesShopPageHeader,
    selectOneRateLabel,
    cancel,
    specialRatesPoints,
    edit,
    addCorpOrPromoCode,
    done,
    clearCTALabel,
  } = useContext(ContentContext);
  const { currentLocale } = useConditionalPageContextSERP();
  const specialRatesList = specialRatesListItems as Array<Record<string, string>>;
  const { specialRateslabel: lowestRatesLabel } =
    specialRatesList?.find(({ specialRatesCode }) => specialRatesCode === 'none') ?? {};
  let specialRateDefaultLabel = '';
  let specialRateDefaultCorpCode = '';
  specialRatesList?.forEach(obj => {
    if (obj['specialRatesCode']?.toLowerCase() === modifiedSplRatesHiddenFields?.clusterCode?.toLowerCase()) {
      specialRateDefaultLabel = obj['specialRateslabel'];
    }
    if (obj['specialRatesCode']?.toLowerCase() === 'corp' && obj['promoRateCode']) {
      specialRateDefaultCorpCode = obj['promoRateCode'];
    }
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const specialRateHeaderLabel: any = ratesShopPageHeader;
  const [specialRateLabel, setSpecialRateLabel] = React.useState<string>(specialRateDefaultLabel);

  useEffect(() => {
    if (popupOpenState) {
      setFocusOnFirstBtn();
    } else {
      //set form values and label
      if (!isMobileView) {
        setValues();
      }
      // Clear activePopup when popup closes
      setActivePopup?.(null);
    }
    if (!(clusterCode?.toLowerCase() === 'corp' || clusterCode === 'group')) {
      setValues();
    }

    //set Corp/Promo code. if any promocode value is configured in AEM, it will be the default value
    //if user edit/add a new promocode, it will be picked as Corp/Promo value
    if (
      (clusterCode?.toLowerCase() === 'corp' || clusterCode === 'group') &&
      (corpCode || specialRateDefaultCorpCode)
    ) {
      setCollapseTextField(true);
      setCorpCode(corpCode ? corpCode : specialRateDefaultCorpCode);
    }
    if (!popupOpenState && clusterCode?.toLowerCase() === 'corp' && !corpCode?.trim()) {
      setCorpCode('');
    }
  }, [popupOpenState, clusterCode]);

  // update local state if any change in formValues
  useEffect(() => {
    const splRates = formValues?.[SPECIAL_RATES];
    setClusterCode(splRates?.clusterCode ?? '');
    setCorpCode(splRates?.corporateCode ?? '');
    setSpecialRateLabel(specialRateDefaultLabel);
  }, [formValues]);

  useEffect(() => {
    // Focus on the textfiled, When corp/group option is selected
    if ((clusterCode?.toLowerCase() === 'corp' || (isADFPage && clusterCode === 'group')) && !collapseTextField) {
      const textField = compRef.current?.querySelector('[name="corporateCode"]') as HTMLElement;
      textField?.focus();
    }
  }, [clusterCode]);

  const applyBtnHandler = (): void => {
    //set form values and label
    setValues();
    // when Apply button is clicked, update mobile state
    if (changeMobileState) {
      changeMobileState();
    }
    if (clusterCode.toLocaleLowerCase() === 'corp' && !corpCode?.trim()) {
      setCorpCode('');
      setFormValues(
        setSpecialRates({
          displayText: '',
          clusterCode: 'none',
          corporateCode: '',
        })
      );
    }
  };

  // to set form values and label
  const setValues = (): void => {
    if ((clusterCode?.toLowerCase() === 'corp' || clusterCode === 'group') && !corpCode) {
      if (
        modifiedSplRatesHiddenFields?.clusterCode?.toLowerCase() === 'corp' ||
        modifiedSplRatesHiddenFields?.clusterCode === 'group'
      ) {
        setClusterCode('none');
        specialRatesList.forEach(obj => {
          if (obj['specialRatesCode'] === 'none') {
            setSpecialRateLabel(obj['specialRateslabel']);
          }
        });
      } else {
        setClusterCode(modifiedSplRatesHiddenFields?.clusterCode ?? 'none');
        setSpecialRateLabel(modifiedSplRatesHiddenFields?.displayText ?? specialRateDefaultLabel);
      }
    }
    // clear the textfield when other options are selected
    if (clusterCode?.toLowerCase() !== 'corp' && clusterCode !== 'group' && corpCode) {
      setCorpCode('');
    }

    setFormValues(
      setSpecialRates({
        displayText:
          (clusterCode?.toLowerCase() === 'corp' || clusterCode === 'group') && corpCode === ''
            ? modifiedSplRatesHiddenFields?.clusterCode === 'none' ||
              !modifiedSplRatesHiddenFields ||
              modifiedSplRatesHiddenFields.clusterCode?.toLowerCase() === 'corp' ||
              modifiedSplRatesHiddenFields.clusterCode === 'group'
              ? lowestRatesLabel
              : modifiedSplRatesHiddenFields.displayText ?? ''
            : specialRateLabel,
        clusterCode:
          (clusterCode?.toLowerCase() === 'corp' || clusterCode === 'group') && corpCode === ''
            ? modifiedSplRatesHiddenFields?.clusterCode === 'none' ||
              !modifiedSplRatesHiddenFields ||
              modifiedSplRatesHiddenFields.clusterCode?.toLowerCase() === 'corp' ||
              modifiedSplRatesHiddenFields.clusterCode === 'group'
              ? 'none'
              : modifiedSplRatesHiddenFields.clusterCode ?? ''
            : clusterCode,
        // update corporateCode state to retain the entered value in the textfield
        corporateCode: clusterCode?.toLowerCase() === 'corp' || (isADFPage && clusterCode === 'group') ? corpCode : '',
      })
    );

    // when cluster code is set to some value other than none then use points will become false
    if (clusterCode !== 'none') {
      setFormValues(
        setUsePoints({
          value: false,
        })
      );
    }
  };

  useEffect(() => {
    // when coporate code is changed update the values in formValues
    setValues();
  }, [corpCode]);

  useEffect(() => {
    const handleResetToLowestRegularRate = () => {
      setFormValues(
        setSpecialRates({
          displayText: lowestRatesLabel ?? 'Lowest Regular Rate',
          clusterCode: 'none',
          corporateCode: '',
        })
      );
    };
    eventUtil.on('resetSpecialRatesToLowestRegularRate', handleResetToLowestRegularRate);
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      eventUtil.remove('resetSpecialRatesToLowestRegularRate', () => {});
    };
  }, []);

  // to show popup on desktop on button click
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const showPopup = (): void => {
    setPopupOpenState(true);
    setIsUserInteractWithComp(true);
    setActivePopup?.('specialRates');
  };

  // Close popup when another popup becomes active
  useEffect(() => {
    if (activePopup !== null && activePopup !== 'specialRates' && popupOpenState) {
      setPopupOpenState(false);
    }
  }, [activePopup]);

  const setFocusOnFirstBtn = (): void => {
    const popupElemBtn = compRef.current?.querySelector(
      ".special-rates-popup .radio-label input[type='radio']:checked"
    ) as HTMLElement;
    popupElemBtn?.focus();
  };

  // to collapse Text field when key is enter or done
  const keyHandler = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      if (!corpCode) {
        const textField = compRef.current?.querySelector('[name="corporateCode"]') as HTMLElement;
        textField?.focus();
      } else {
        setCollapseTextField(true);
      }
    }
  };

  // to clear text input field
  const resetHandler = (): void => {
    const textField = compRef.current?.querySelector('[name="corporateCode"]') as HTMLElement;
    textField?.focus();
    setCorpCode('');
  };

  // to show input text field on button click
  const editHandler = (clusterCode: string): void => {
    setCollapseTextField(false);
    setClusterCode(clusterCode);
  };

  // to close popup on desktop on button click
  const collapseField = (): void => {
    setCollapseTextField(true);
    const corpFiled = compRef.current?.querySelector('[value="corp"]') as HTMLElement;
    corpFiled?.focus();
  };

  // to handle radioButton selections
  const changeHandler = (value: string, label: string): void => {
    setClusterCode(value);
    setSpecialRateLabel(label);
    setCorpCode('');
    // to show the textfiled when corp or group option is selected
    if (value?.toLowerCase() === 'corp' || (isADFPage && value === 'group')) {
      setCollapseTextField(false);
    }
  };

  return (
    <StyledSpecialRatesDiv
      ref={compRef}
      className={clsx(
        'standard',
        `browser-outline`,
        ` ${isSearchPage && !isMobileView && 'search-rates'}`,
        `${clusterCode?.toLowerCase() === 'corp' ? (corpCode ? `custom-code-apply` : '') : ''}`,
        `${isADFPage && !isMobileView && 'adf-rates'}`
      )}
    >
      {isMobileView
        ? ''
        : !isSearchPage && (
            <>
              <button
                type="button"
                className={clsx(
                  't-font-s',
                  isSearchPage ? 'search-button' : '',
                  'custom_click_track',
                  currentLocale === hardCodedAllLocale.de ? 'displayEllipisis' : ''
                )}
                onClick={showPopup}
                aria-expanded={popupOpenState}
              >
                {specialRateLabel}
                {clusterCode?.toLowerCase() === 'corp' || (isADFPage && clusterCode === 'group') // to show the entered corpCode when we click outside the popup
                  ? corpCode
                    ? ' - ' + corpCode
                    : ''
                  : ''}
              </button>
              <div className="icon-dropdown-down"></div>
            </>
          )}
      {isSearchPage || isMobileView || popupOpenState ? (
        <Popup
          className={clsx(
            'special-rates-popup custom-scrollbar',
            popupOpenState ? 'open-popup-header' : 'close-popup-header',
            isADFPage ? 'adf-special-rates' : ''
          )}
          show={true}
          handleBlur={!isMobileView}
          popupOpenState={popupOpenState}
          setPopupOpenState={setPopupOpenState}
        >
          <fieldset>
            {!isMobileView ? (
              <legend className="t-label-s">
                <PopupHeader
                  labelIcon=""
                  labelText={isSearchPage ? specialRateHeaderLabel || ratesShopPageHeader : selectOneRateLabel}
                  cancelLabel=""
                  removeScreenReaderClass={true}
                  className={clsx(isADFPage ? '' : 'pt-1')}
                >
                  {!popupOpenState && isSearchPage && (
                    <div
                      className="special-rates-dropdown custom_click_track"
                      onClick={showPopup}
                      custom_click_track_value={`${getSearchFormClickTrackingLoc()}| Special Rates Field |internal`}
                    >
                      <button
                        type="button"
                        aria-expanded={popupOpenState}
                        className={clsx(
                          't-font-s',
                          isSearchPage ? 'search-button' : '',
                          currentLocale === hardCodedAllLocale.de ? 'displayEllipisis' : ''
                        )}
                      >
                        {specialRateLabel}
                        {clusterCode?.toLowerCase() === 'corp' || (isADFPage && clusterCode === 'group') // to show the entered corpCode when we click outside the popup
                          ? corpCode
                            ? ' - ' + (isADFPage ? corpCode.toUpperCase() : corpCode)
                            : ''
                          : ''}
                      </button>
                      <div className="icon-dropdown-down"></div>
                    </div>
                  )}
                </PopupHeader>
              </legend>
            ) : (
              <PopupHeader
                cancelLabel={cancel}
                cancelClickHandler={onCancel}
                labelIcon=""
                labelText={isSearchPage ? specialRateHeaderLabel || ratesShopPageHeader : specialRatesPoints}
                mainText={`${specialRateLabel}${
                  clusterCode?.toLowerCase() === 'corp' || (isADFPage && clusterCode === 'group') // to show the entered corpCode when we click outside the popup
                    ? corpCode
                      ? ' - ' + corpCode
                      : ''
                    : ''
                }`}
                removeScreenReaderClass={true}
              />
            )}
            {(popupOpenState || isMobileView) && (
              <PopupMain>
                {specialRatesList?.map((element: Record<string, string>, index: number) => {
                  // to stop group option being shown on the SERP Page
                  if (!isADFPage && element['specialRatesCode']?.toLowerCase() === 'group') {
                    return null;
                  }
                  return (
                    <div key={element['specialRatesCode']} className="spl-rates-radio-container">
                      <RadioButton
                        aria-label={element['specialRateslabel']}
                        customClickClass="custom_click_track"
                        radiobuttonLabel={element['specialRateslabel']}
                        className={clsx(
                          (element['specialRatesCode']?.toLowerCase() === 'corp' ||
                            (isADFPage && element['specialRatesCode']?.toLowerCase() === 'group')) && // to move the corp/group label up when corpCode is entered and done button is clicked
                            collapseTextField &&
                            corpCode
                            ? 'corp-code-edit'
                            : '',
                          'radio-label'
                        )}
                        labelClassName={clsx(
                          element['specialRatesCode']?.toLowerCase() === 'corp' ? 'corp-edit-label' : ''
                        )}
                        setTabIndex={-1}
                        radiobuttonName={element['specialRatesCode']}
                        radiobuttonId={`'clusterCode' + ${index}`}
                        checked={clusterCode?.toLowerCase() === element['specialRatesCode']?.toLowerCase()}
                        value={element['specialRatesCode']}
                        onChange={({ target: { value } }): void => changeHandler(value, element['specialRateslabel'])}
                        custom_click_track_value={`${getSearchFormClickTrackingLoc()}| ${
                          element['specialRateslabel']
                        } |internal`}
                      />
                      {(element['specialRatesCode']?.toLowerCase() === 'corp' ||
                        (isADFPage && element['specialRatesCode']?.toLowerCase() === 'group')) && // to show the corpcode entered under corp/group option when clicked on done button
                      element['specialRatesCode']?.toLowerCase() === clusterCode?.toLowerCase() &&
                      collapseTextField &&
                      corpCode ? (
                        <div
                          className={isSearchPage ? 'corp-code-container search-code-container' : 'corp-code-container'}
                        >
                          <span>{corpCode}</span>
                          <button
                            type="button"
                            className="cta-link custom_click_track"
                            custom_click_track_value={`${getSearchFormClickTrackingLoc()}| ${SPECIAL_RATES_EDIT_CLICK_TRACK}`}
                            onClick={() => editHandler(clusterCode)}
                          >
                            {edit}
                          </button>
                        </div>
                      ) : (
                        ''
                      )}
                      {(element['specialRatesCode']?.toLowerCase() === 'corp' ||
                        (isADFPage && element['specialRatesCode']?.toLowerCase() === 'group')) && // to show the textfield when corp/group option is selected
                        element['specialRatesCode']?.toLowerCase() === clusterCode?.toLowerCase() &&
                        !collapseTextField && (
                          <div
                            className={
                              isSearchPage && !isMobileView
                                ? 'crop-promo-code search-form-promo-code'
                                : 'crop-promo-code'
                            }
                          >
                            <TextField
                              className="t-overline-normal"
                              textfieldLabel={addCorpOrPromoCode}
                              textfieldName="corporateCode"
                              textfieldId={'corpCode' + CommonUtils.getElementId()}
                              value={corpCode}
                              // eslint-disable-next-line jsx-a11y/no-autofocus
                              autoFocus={true}
                              onChange={({ target: { value } }): void => {
                                setCorpCode(value);
                              }}
                              onKeyPress={keyHandler}
                              maxLength={9}
                              inputClassName={'corp-code-textbox'}
                            />
                            {corpCode ? (
                              <button
                                type="button"
                                className="link-white cta-reset icon-cancel custom_click_track"
                                onClick={resetHandler}
                                aria-label="Clear corp/promo code"
                                custom_click_track_value={`${getSearchFormClickTrackingLoc()}| ${SPECIAL_RATES_CLOSE_CLICK_TRACK}`}
                              >
                                <span className="sr-only">{clearCTALabel}</span>
                              </button>
                            ) : (
                              ''
                            )}
                            {!isMobileView && corpCode && (
                              <button
                                type="button"
                                onClick={collapseField}
                                className="m-button-secondary m-button-s custom_click_track"
                                custom_click_track_value={`${getSearchFormClickTrackingLoc()}| ${SPECIAL_RATES_DONE_CLICK_TRACK}`}
                              >
                                {done}
                              </button>
                            )}
                          </div>
                        )}
                    </div>
                  );
                })}
              </PopupMain>
            )}
            {isMobileView ? <PopupFooter primaryBtnLabel={done} primaryBtnClickHandler={applyBtnHandler} /> : ''}
          </fieldset>
        </Popup>
      ) : (
        ''
      )}
    </StyledSpecialRatesDiv>
  );
};

export default SpecialRates;
